import React, { useState, useMemo, useEffect } from 'react';
import useAppStore, { useCalculationsSelector, ULMonatsDaten } from '../store/appStore';
import { useShallow } from 'zustand/react/shallow';
import PasswordPromptModal from '../components/PasswordPrompt'; 
import EditableField from '../components/EditableField'; 
import UI from '../components/UI';
import FestsetzungsHistorieModalUnternehmer, { UnternehmerFestsetzungHistorieEntry } from '../components/FestsetzungsHistorieModalUnternehmer';
import { isHj1ClientLocked } from '../utils/hjLock';
import Hj1MandantLockBanner from '../components/Hj1MandantLockBanner';

type MitarbeiterStatus = 'neutral' | 'ok' | 'warnung' | 'schlecht';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SafePasswordPrompt = PasswordPromptModal as any;

// ============================================================================
// STYLES: EXECUTIVE CARBON EDITION (NOBLE SLATE v11.0)
// ============================================================================
const DashboardStyles = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700;800&family=Inter:wght@300;400;600;800&display=swap');

        .font-sans { font-family: 'Inter', sans-serif; }
        .font-mono-numbers { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        
        .bg-deep-space { background-color: #030303; }
        html:not(.dark) .bg-deep-space { background-color: var(--pe-bg-primary); }
        html:not(.dark) .carbon-weave-bg { background-color: var(--pe-bg-elevated); background-image: none; }
        html:not(.dark) .carbon-panel { background-color: var(--pe-bg-card); border-color: var(--pe-border-subtle); }
        html:not(.dark) .carbon-panel::after { background: none; }
        .text-phantom-gold { color: #E6C299; }
        
        .carbon-weave-bg {
            background-color: #111;
            background-image: 
                radial-gradient(circle at 100% 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 0 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 50% 100%, #1a1a1a 10%, #111 10%, #111 23%, #1a1a1a 23%, #1a1a1a 30%, #111 30%, #111 43%, #1a1a1a 43%, #1a1a1a 50%, #111 50%, #111 63%, #1a1a1a 63%, #1a1a1a 71%, transparent 71%, transparent),
                radial-gradient(circle at 100% 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent),
                radial-gradient(circle at 0 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent);
            background-size: 20px 20px;
        }

        .carbon-panel {
            position: relative;
            border-radius: 1.5rem;
            border: 1px solid #2a2a2a;
            box-shadow: 0 30px 60px -15px rgba(0,0,0,1), inset 0 2px 5px rgba(255,255,255,0.05);
        }

        .carbon-panel::after {
            content: ''; position: absolute; inset: 0; background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.6)); border-radius: inherit; pointer-events: none;
        }

        .carbon-content { position: relative; z-index: 10; }

        html:not(.dark) .bg-deep-space { background-color: #F4F2EF; }
        html:not(.dark) .carbon-weave-bg {
            background-image: none;
            background-color: #FAF9F7;
        }
        html:not(.dark) .carbon-panel {
            border: 1px solid #D6D3D1;
            box-shadow: 0 12px 28px -12px rgba(28, 25, 23, 0.18);
            background-color: #FFFFFF;
        }
        html:not(.dark) .carbon-panel::after {
            background: none;
        }
        html:not(.dark) .metal-badge {
            background: linear-gradient(to bottom, #FAF9F7, #EDEAE6);
            border: 1px solid #D6D3D1;
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.8), 0 2px 6px rgba(28,25,23,0.06);
        }

        /* NEON LED COLORS */
        .led-purple  { color: #c084fc; text-shadow: 0 0 10px rgba(192,132,252,0.6), 0 0 20px rgba(192,132,252,0.3); }
        .led-emerald { color: #10b981; text-shadow: 0 0 10px rgba(16,185,129,0.6), 0 0 20px rgba(16,185,129,0.3); }
        .led-lime    { color: #a3e635; text-shadow: 0 0 10px rgba(163,230,53,0.6),  0 0 20px rgba(163,230,53,0.3); }
        .led-amber   { color: #fbbf24; text-shadow: 0 0 10px rgba(251,191,36,0.6),  0 0 20px rgba(251,191,36,0.3); }
        .led-orange  { color: #fb923c; text-shadow: 0 0 10px rgba(251,146,60,0.6),  0 0 20px rgba(251,146,60,0.3); }
        .led-red     { color: #ef4444; text-shadow: 0 0 10px rgba(239,68,68,0.6),   0 0 20px rgba(239,68,68,0.3); }
        .led-gold    { color: #E6C299; text-shadow: 0 0 10px rgba(230,194,153,0.6), 0 0 20px rgba(230,194,153,0.3); }
        .led-white   { color: #fff; text-shadow: 0 0 15px rgba(255,255,255,0.5); }

        /* LIGHT-MODE: LED-Farben abgedunkelt, ohne Glow (Dark bleibt unangetastet) */
        html:not(.dark) .led-purple  { color: #6D28D9; text-shadow: none; }
        html:not(.dark) .led-emerald { color: #047857; text-shadow: none; }
        html:not(.dark) .led-lime    { color: #4D7C0F; text-shadow: none; }
        html:not(.dark) .led-amber   { color: #B45309; text-shadow: none; }
        html:not(.dark) .led-orange  { color: #C2410C; text-shadow: none; }
        html:not(.dark) .led-red     { color: #B91C1C; text-shadow: none; }
        html:not(.dark) .led-gold    { color: var(--pe-accent-text); text-shadow: none; }
        html:not(.dark) .led-white   { color: #1C1917; text-shadow: none; }

        .metal-badge {
            background: linear-gradient(to bottom, #222, #0a0a0a);
            border: 1px solid #333;
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 10px rgba(0,0,0,0.8);
            border-radius: 6px;
        }
    `}</style>
);

// ============================================================================
// HILFSFUNKTIONEN
// ============================================================================

const getEmptyHJ1 = () => ["Januar", "Februar", "März", "April", "Mai", "Juni"].map(m => ({ monat: m, bedienumsatzBrutto: 0, vkUmsatzBrutto: 0, urlaubsTage: 0, krankheitsTage: 0, festmachen: false }));
const getEmptyHJ2 = () => ["Juli", "August", "September", "Oktober", "November", "Dezember"].map(m => ({ monat: m, bedienumsatzBrutto: 0, vkUmsatzBrutto: 0, urlaubsTage: 0, krankheitsTage: 0, festmachen: false }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getSafeVal = (m: any, keyLower: string, keyCamel: string): number => {
    const val = m?.[keyLower] ?? m?.[keyCamel];
    if (val === null || val === undefined || val === '') return 0;
    if (typeof val === 'number') return val;
    return parseFloat(String(val).replace(/\./g, '').replace(',', '.')) || 0;
};

const COUNT_HJ = 6;

const safeDiv = (numerator: number, denominator: number): number => {
    const num = Number(numerator);
    const den = Number(denominator);
    if (!den || isNaN(num) || isNaN(den)) return 0;
    return num / den;
};

const getVkFaktorColor = (wertProzent: number): string => {
    if (isNaN(wertProzent) || wertProzent <= 0) return 'text-stone-500 dark:text-slate-500'; 
    if (wertProzent >= 15) return 'text-purple-400 led-purple';
    if (wertProzent >= 10) return 'text-emerald-400 led-emerald';
    if (wertProzent >= 6.9) return 'text-lime-400 led-lime';
    if (wertProzent >= 5) return 'text-amber-400 led-amber';
    if (wertProzent >= 3) return 'text-orange-400 led-orange';
    return 'text-red-400 led-red';
};

const getUmsatzAuslastungStyles = (val: number) => {
    const p = val <= 5 ? val * 100 : val;
    
    if (isNaN(p) || p === 0) return { text: 'text-stone-500 dark:text-slate-500', border: 'border-pe-border-strong' };
    if (p >= 85) return { text: 'text-purple-400 led-purple', border: 'border-purple-500/50' };
    if (p >= 75) return { text: 'text-emerald-400 led-emerald', border: 'border-emerald-500/50' };
    if (p >= 70) return { text: 'text-lime-400 led-lime', border: 'border-lime-500/50' };
    if (p >= 60) return { text: 'text-amber-400 led-amber', border: 'border-amber-500/50' };
    if (p >= 50) return { text: 'text-orange-400 led-orange', border: 'border-orange-500/50' };
    return { text: 'text-red-400 led-red', border: 'border-red-500/50' };
};

const getMitarbeiterStatus = (umsatz: number, soll: number, kostendeckung: number): MitarbeiterStatus => {
    if (umsatz <= 0) return 'neutral';
    if (umsatz >= soll) return 'ok';
    if (umsatz >= kostendeckung) return 'warnung';
    return 'schlecht';
};

// ============================================================================
// UI-KOMPONENTEN
// ============================================================================

const FormulaDisplay: React.FC<{
    show?: boolean;
    value: number | string;
    formula: string;
    type?: 'currency' | 'percentage' | 'number' | 'text';
    decimalPlaces?: number;
    suffix?: string;
    highlightNegative?: boolean;
    currencyCode?: string;
    bgColor?: string;
    textColor?: string;
    className?: string;
}> = ({ show = false, value, formula, type = 'currency', decimalPlaces, suffix, highlightNegative = false, currencyCode = 'EUR', bgColor, textColor, className }) => {
    
    let activeDecimals = decimalPlaces;
    if (activeDecimals === undefined) {
        if (type === 'currency') activeDecimals = 0;
        else if (type === 'percentage') activeDecimals = 1;
        else activeDecimals = 1;
    }

    if (show) {
        return <div className="bg-pe-tile-header dark:bg-black text-purple-700 dark:text-purple-400 led-purple text-xs font-bold p-1 rounded border border-pe-border-strong text-center w-full h-full flex items-center justify-center">{formula}</div>;
    }

    let displayValue = "-";
    const num = Number(value);
    
    if (type === 'text') displayValue = String(value);
    else if (!isNaN(num)) {
        if (type === 'percentage') {
             displayValue = new Intl.NumberFormat('de-DE', { style: 'percent', minimumFractionDigits: activeDecimals, maximumFractionDigits: activeDecimals }).format(num);
        } else {
             displayValue = new Intl.NumberFormat('de-DE', { minimumFractionDigits: activeDecimals, maximumFractionDigits: activeDecimals }).format(num);
        }
    }

    let finalSuffix = suffix;
    if (type === 'currency' && !suffix) finalSuffix = currencyCode;

    let finalColor = textColor || 'text-pe-accent-text';
    if (highlightNegative && num < 0) finalColor = 'text-red-400 led-red';

    return (
        <div className={`w-full h-8 px-1 flex items-center justify-end font-bold font-mono-numbers text-base ${bgColor || 'bg-transparent'} ${finalColor} border-none rounded relative ${className}`}>
            <span className={`w-full text-right ${finalSuffix ? 'pr-8' : 'pr-1'}`}>{displayValue}</span>
            {finalSuffix && <span className="absolute right-1 text-[10px] uppercase tracking-widest text-stone-500 dark:text-slate-500 font-bold font-sans pointer-events-none">{finalSuffix}</span>}
        </div>
    );
};

const UmsatzIndicatorWithText = ({ umsatz, soll, kostendeckung }: { umsatz: number, soll: number, kostendeckung: number }) => {
    if (umsatz <= 0) return <div className="h-full w-full"></div>;
    const status = getMitarbeiterStatus(umsatz, soll, kostendeckung);
    let icon, text, textColor, tooltipText;

    if (status === 'ok') {
        icon = <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.993 0-1.854-.656-2.148-1.595l-2-6A2 2 0 018.25 10V5a2 2 0 012-2h2.5a2 2 0 012 2v5z" /></svg>;
        text = 'Über Plan'; textColor = 'text-emerald-400 led-emerald';
        tooltipText = "Hervorragend! Der Plan-Umsatz wurde erreicht oder übertroffen.";
    } else if (status === 'warnung') {
        icon = <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
        text = 'Kosten gedeckt'; textColor = 'text-amber-400 led-amber';
        tooltipText = "Gut! Die Kosten sind gedeckt, der Plan wurde aber noch nicht erreicht.";
    } else {
        icon = <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.738 3h4.017c.993 0 1.854-.656-2.148-1.595l2 6A2 2 0 0116.75 14v5a2 2 0 01-2-2h-2.5a2 2 0 01-2-2v-5z" /></svg>;
        text = 'Verlustzone'; textColor = 'text-red-400 led-red';
        tooltipText = "Achtung! Der Umsatz deckt die Kosten nicht (Unterdeckung).";
    }
    
    return (
        <div className={`flex flex-col items-center justify-center space-y-1 p-1 h-full cursor-help`} data-tooltip-id="app-tooltip" data-tooltip-content={tooltipText}>
            <div className={`flex-shrink-0 ${textColor}`}>{icon}</div>
            <div className={`text-[9px] uppercase tracking-widest font-bold text-center leading-tight ${textColor}`}>{text}</div>
        </div>
    );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CompactTile = ({ label, value, type = 'text', width = 'w-28', textColor, bgColor = 'bg-pe-card', isFactor=false, suffix='', tooltip, currencyCode = 'EUR', decimalPlaces }: any) => {
   const valueColor = textColor ?? ((type === 'currency' || isFactor) ? 'text-pe-accent-text' : 'text-stone-900 dark:text-white');
   return (
   <div className={`flex flex-col ${width} ${bgColor} border border-stone-200 dark:border-pe-border-strong rounded-lg overflow-hidden cursor-help shadow-inner`} data-tooltip-id={tooltip ? "app-tooltip" : undefined} data-tooltip-content={tooltip}>
       <div className={`text-xs font-semibold text-pe-muted uppercase tracking-wider bg-stone-100 dark:bg-pe-tile-header px-2 py-1.5 text-center border-b border-stone-200 dark:border-pe-border-strong whitespace-nowrap overflow-hidden text-ellipsis`}>{label}</div>
       <div className="flex-grow flex items-center justify-center p-1.5 pointer-events-none">
            <EditableField 
                value={value} 
                readOnly={true}
                transparent={true} 
                type={type === 'text' ? 'text' : (type === 'currency' ? 'currency' : 'number')}
                currency={currencyCode} 
                suffix={suffix}
                variant={type === 'currency' ? 'revenue' : (label.includes('Std') ? 'hours' : 'standard')} 
                decimalPlaces={decimalPlaces !== undefined ? decimalPlaces : (type === 'currency' ? 0 : (isFactor ? 2 : undefined))}
                className={`justify-center font-bold font-mono-numbers ${valueColor} text-base`}
            />
       </div>
   </div>
   );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const UnternehmerKennzahlenSektion: React.FC<any> = ({ title = 'Stammdaten (Unternehmer)', echterUnternehmername, echterPrdUnternehmerLohn, echterPlanumsatzBrutto, calculations, currencyCode }) => {
    return (
        <div className="mb-6 p-5 carbon-panel border border-pe-border-strong w-fit bg-pe-card dark:bg-[#030303]">
            <div className="text-[12px] font-black uppercase tracking-widest text-pe-text mb-5 border-b border-pe-border-strong pb-2 carbon-content carbon-weave-bg px-3 py-2 rounded-lg">{title}</div>
            <div className="carbon-content flex flex-wrap gap-3 mb-5">
                <CompactTile label="Unternehmer" value={echterUnternehmername} width="w-48" type="text" tooltip="Name des Unternehmers." />
                <div className="w-2"></div>
                <CompactTile label="Arbeitstage" value={calculations.arbeitstageWoche} decimalPlaces={0} suffix=" T" width="w-24" type="number" tooltip="Reguläre Arbeitstage pro Woche." />
                <CompactTile label="Wochenstd." value={calculations.wochenstunden} decimalPlaces={1} suffix=" Std" width="w-24" type="number" tooltip="Produktive Wochenstunden am Kunden." />
                <CompactTile label="Monatsstd." value={calculations.monatsstunden} decimalPlaces={1} suffix=" Std" width="w-32" type="number" tooltip="Monatliche produktive Stunden (Wochenstd. x Faktor)." />
                <CompactTile label="Ø Std/Tag" value={calculations.arbeitszeitTag} decimalPlaces={1} suffix=" Std" width="w-24" type="number" tooltip="Durchschnittliche Arbeitszeit pro regulärem Arbeitstag." />
            </div>
            <div className="carbon-content flex flex-wrap gap-3">
                <CompactTile label="Prd. U-Lohn" value={echterPrdUnternehmerLohn} type="currency" width="w-32" tooltip="Der kalkulierte produktive Unternehmerlohn für diesen Monat." currencyCode={currencyCode} />
                <CompactTile label="Std. Lohn" value={calculations.stdLohn} type="currency" width="w-32" decimalPlaces={2} tooltip="Unternehmerlohn heruntergebrochen auf eine Stunde." currencyCode={currencyCode} />
                <div className="w-2 border-l border-pe-border-strong mx-1"></div>
                
                <CompactTile label="Planumsatz" value={echterPlanumsatzBrutto} type="currency" width="w-32" textColor="text-phantom-gold" tooltip="Gesamter Ziel-Umsatz Brutto pro Monat." currencyCode={currencyCode} />
                
                <CompactTile label="Soll p. Std" value={calculations.sollumsatzPStd} type="currency" width="w-28" decimalPlaces={0} textColor="text-orange-400 led-orange" tooltip="Notwendiger Umsatz pro gearbeiteter Stunde." currencyCode={currencyCode} />
                <CompactTile label="Soll @ 75%" value={calculations.sollumsatzPStd75} type="currency" width="w-28" decimalPlaces={0} tooltip="Notwendiger Stundenumsatz, wenn eine Auslastung von 75% angenommen wird." currencyCode={currencyCode} />
                <CompactTile label="Soll / Tag" value={calculations.sollumsatzProTag} type="currency" width="w-28" decimalPlaces={0} tooltip="Notwendiger Umsatz pro Tag." currencyCode={currencyCode} />
            </div>
        </div>
    );
};

// ============================================================================
// TABLE HELPERS
// ============================================================================

const HeaderCell: React.FC<React.ThHTMLAttributes<HTMLTableCellElement>> = ({ children, className = "", ...props }) => (
    <th className={`p-2 align-middle text-center border-r border-pe-border dark:border-[#222] text-[10px] uppercase tracking-widest font-bold text-pe-muted dark:text-slate-400 overflow-hidden text-ellipsis ${className}`} {...props}>{children}</th>
);

const DataCell: React.FC<React.TdHTMLAttributes<HTMLTableCellElement>> = ({ children, className = "", ...props }) => (
    <td className={`p-1 border-r border-pe-border dark:border-[#222] border-b border-pe-border-strong relative ${className}`} {...props}>{children}</td>
);

const FooterCell = ({ width, children, className = "", bg = "" }: { width: number, children?: React.ReactNode, className?: string, bg?: string }) => (
    <div style={{ width: `${width}px`, minWidth: `${width}px` }} className={`p-1 flex items-center justify-center border-r border-pe-border-strong relative overflow-visible ${bg} ${className}`}>
        {children}
    </div>
);

const COLS = {
    monat: 160, bedien: 170, vk: 170, vkFaktor: 90, urlaub: 90, krank: 90, gesamt: 170, ampel: 170, umsatzStd: 150, plan: 170, netto: 170, gap: 25, fest: 70
};

const TOTAL_WIDTH = Object.values(COLS).reduce((a, b) => a + b, 0);

const mapUnternehmerHistorieEntries = (halbjahrData: ULMonatsDaten[]): UnternehmerFestsetzungHistorieEntry[] =>
    (halbjahrData || [])
        .filter((m) => m?.festmachen)
        .map((m) => {
            const metrics = m.lockedSnapshot?.metrics;
            const planumsatz = metrics?.planumsatz;
            const monatsstunden = metrics?.monatsstunden;
            const sollFallback = planumsatz && monatsstunden ? planumsatz / monatsstunden : undefined;

            return {
                monat: m.monat || 'Unbekannt',
                lockedAt: m.lockedSnapshot?.lockedAt,
                lockedBy: m.lockedSnapshot?.lockedBy,
                snapshot: metrics ? {
                    arbeitstage: metrics.arbeitstage ?? metrics.arbeitstageWoche,
                    wochenstunden: metrics.wochenstunden,
                    stdLohn: metrics.stdLohn,
                    planumsatz,
                    sollumsatzPStd: metrics.sollumsatzPStd ?? sollFallback,
                } : undefined,
            };
        });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const HalbjahrTabelleSektion: React.FC<any> = ({ title, halbjahrKey, data, localUpdateULMonat, showFormulas, calculations, onToggleFestmachen, planumsatzBrutto, mwstProzent, auslastungsFaktor, currencyCode, kostendeckungsumsatz, isHalbjahrInputLocked = false }) => {
    
    const handleUpdate = (index: number, field: keyof ULMonatsDaten, value: number | boolean | string) => { 
        localUpdateULMonat(halbjahrKey, index, field, value); 
    };
    
    const handleFestmachenChange = (e: React.MouseEvent<HTMLInputElement>, index: number, currentCheckedState: boolean) => { 
        e.preventDefault(); 
        onToggleFestmachen(halbjahrKey, index, currentCheckedState); 
    };

    const { monatsDetails, summary, average, umsatzAuslastung } = useMemo(() => {
        const monatsDetails = data.map((monat: ULMonatsDaten, index: number) => {
            const isLocked = monat.festmachen === true;
            const rowSnapMetrics = monat.lockedSnapshot?.metrics;

            const activeMonatsstunden = (isLocked && rowSnapMetrics?.monatsstunden !== undefined) ? rowSnapMetrics.monatsstunden : calculations.monatsstunden;
            const activePlanumsatz = (isLocked && rowSnapMetrics?.planumsatz !== undefined) ? rowSnapMetrics.planumsatz : planumsatzBrutto;
            const activeMwst = (isLocked && rowSnapMetrics?.mwst !== undefined) ? rowSnapMetrics.mwst : mwstProzent;
            const activeKostendeckung = (isLocked && rowSnapMetrics?.kostendeckung !== undefined) ? rowSnapMetrics.kostendeckung : kostendeckungsumsatz;

            const activeSollumsatzPStd = activeMonatsstunden > 0 ? activePlanumsatz / activeMonatsstunden : 0;

            const bedien = getSafeVal(monat, 'bedienumsatzBrutto', 'bedienUmsatz');
            const vk = getSafeVal(monat, 'vkUmsatzBrutto', 'vkUmsatz');
            const hasUmsatz = (bedien + vk) > 0;
            const hasDays = (getSafeVal(monat, 'urlaubsTage', 'urlaubsTage') > 0) || (getSafeVal(monat, 'krankheitsTage', 'krankheitsTage') > 0);
            const isZeileLeer = !hasUmsatz && !hasDays;
            const effectiveUrlaubsTage = hasUmsatz ? monat.urlaubsTage : 0;
            
            return { 
                isZeileLeer, hasUmsatz, effectiveUrlaubsTage, vkFaktor: 0, gesamtumsatzBrutto: 0, umsatzPStd: 0, deltaNetto: 0, 
                bedien, vk, activeMonatsstunden, activePlanumsatz, activeMwst, activeKostendeckung, activeSollumsatzPStd 
            };
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        monatsDetails.forEach((details: any) => {
            if (details.isZeileLeer) { 
                details.vkFaktor = NaN; details.gesamtumsatzBrutto = NaN; details.umsatzPStd = NaN; details.deltaNetto = NaN; 
            } else { 
                details.vkFaktor = safeDiv(details.vk, details.bedien); 
                details.gesamtumsatzBrutto = details.bedien + details.vk; 
                details.umsatzPStd = safeDiv(details.gesamtumsatzBrutto, details.activeMonatsstunden); 
                if (details.gesamtumsatzBrutto === 0) {
                    details.deltaNetto = 0;
                } else {
                    details.deltaNetto = safeDiv(details.gesamtumsatzBrutto - details.activePlanumsatz, (1 + details.activeMwst / 100)); 
                }
            }
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const summary = data.reduce((acc: any, _monat: any, index: number) => {
            const details = monatsDetails[index];
            const originalRow = data[index];
            if (details.isZeileLeer) return acc; 
            
            acc.bedienumsatzBrutto += details.bedien; 
            acc.vkUmsatzBrutto += details.vk;
            
            acc.urlaubsTage += Number(originalRow.urlaubsTage) || 0; 
            acc.krankheitsTage += Number(originalRow.krankheitsTage) || 0; 
            
            acc.gesamtumsatzBrutto += (details.gesamtumsatzBrutto || 0); 
            acc.deltaNetto += (details.deltaNetto || 0); 
            
            acc.umsatzPStd += (details.umsatzPStd || 0);
            acc.activePlanumsatz += (details.activePlanumsatz || 0);
            acc.activeKostendeckung += (details.activeKostendeckung || 0);
            
            return acc;
        }, { bedienumsatzBrutto: 0, vkUmsatzBrutto: 0, urlaubsTage: 0, krankheitsTage: 0, gesamtumsatzBrutto: 0, deltaNetto: 0, umsatzPStd: 0, activePlanumsatz: 0, activeKostendeckung: 0 });

        const monthsWithSales = data.filter((m: ULMonatsDaten) => (getSafeVal(m, 'bedienumsatzBrutto', 'bedienUmsatz') + getSafeVal(m, 'vkUmsatzBrutto', 'vkUmsatz')) > 0).length || 1;
        
        let totalActiveSollumsatz = 0;
        let activeMonthsCount = 0;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        monatsDetails.forEach((d: any) => {
            if (!d.isZeileLeer) {
                totalActiveSollumsatz += d.activeSollumsatzPStd;
                activeMonthsCount++;
            }
        });
        const avgActiveSollumsatz = safeDiv(totalActiveSollumsatz, activeMonthsCount || 1);

        const average = { 
            vkFaktor: safeDiv(summary.vkUmsatzBrutto, summary.bedienumsatzBrutto), 
            bedienumsatzBrutto: safeDiv(summary.bedienumsatzBrutto, monthsWithSales), 
            vkUmsatzBrutto: safeDiv(summary.vkUmsatzBrutto, monthsWithSales), 
            urlaubsTage: safeDiv(summary.urlaubsTage, COUNT_HJ), 
            krankheitsTage: safeDiv(summary.krankheitsTage, COUNT_HJ), 
            gesamtumsatzBrutto: safeDiv(summary.gesamtumsatzBrutto, monthsWithSales), 
            umsatzPStd: safeDiv(summary.umsatzPStd, monthsWithSales), 
            deltaNetto: safeDiv(summary.deltaNetto, monthsWithSales),
            activePlanumsatz: safeDiv(summary.activePlanumsatz, monthsWithSales),
            activeKostendeckung: safeDiv(summary.activeKostendeckung, monthsWithSales)
        };
        
        const umsatzAuslastung = auslastungsFaktor * safeDiv(average.umsatzPStd, avgActiveSollumsatz);
        
        return { monatsDetails, summary, average, umsatzAuslastung };
    }, [data, calculations, planumsatzBrutto, mwstProzent, auslastungsFaktor, kostendeckungsumsatz]);

    const { text: auslastungColor, border: auslastungBorder } = getUmsatzAuslastungStyles(umsatzAuslastung);
    const vkFaktorTooltip = "Verhältnis von Verkaufs- zu Bedienumsatz. Lila: ≥15%, Grün: ≥10%, Hellgrün: ≥6.9%, Gelb: ≥5%, Orange: ≥3%, Rot: <3%.";

    return (
        <div className="mb-10 carbon-panel border border-pe-border-strong shadow-2xl overflow-hidden inline-block bg-pe-card dark:bg-[#030303]">
            <div className="text-[12px] font-black uppercase tracking-widest p-4 text-pe-text border-b-2 border-pe-border-strong carbon-content carbon-weave-bg">{title}</div>
            
            <div className="carbon-content pb-4">
                <table className="table-fixed border-collapse text-center font-bold bg-transparent" style={{ width: `${TOTAL_WIDTH}px`, minWidth: `${TOTAL_WIDTH}px` }}>
                    <colgroup>
                        <col style={{ width: `${COLS.monat}px` }} /> <col style={{ width: `${COLS.bedien}px` }} /> <col style={{ width: `${COLS.vk}px` }} /> <col style={{ width: `${COLS.vkFaktor}px` }} />  <col style={{ width: `${COLS.urlaub}px` }} />  <col style={{ width: `${COLS.krank}px` }} />  <col style={{ width: `${COLS.gesamt}px` }} /> <col style={{ width: `${COLS.ampel}px` }} /> <col style={{ width: `${COLS.umsatzStd}px` }} /> <col style={{ width: `${COLS.plan}px` }} /> <col style={{ width: `${COLS.netto}px` }} /> <col style={{ width: `${COLS.gap}px` }} /> <col style={{ width: `${COLS.fest}px` }} />   
                    </colgroup>
                    
                    <thead className="bg-pe-tile-header text-pe-muted dark:text-slate-400 border-b-2 border-pe-border-strong">
                        <tr className="items-stretch">
                            <HeaderCell className="sticky left-0 z-30 bg-pe-tile-header border-r-2 border-pe-border dark:border-[#222]">Monat</HeaderCell>
                            <HeaderCell>Bedien<br/>brutto</HeaderCell>
                            <HeaderCell>VK<br/>brutto</HeaderCell>
                            <HeaderCell>VK-<br/>Faktor</HeaderCell>
                            <HeaderCell className="text-blue-500/80">Urlaub<br/>(Tage)</HeaderCell>
                            <HeaderCell className="text-red-500/80">Krank<br/>(Tage)</HeaderCell>
                            <HeaderCell>Gesamt<br/>brutto</HeaderCell>
                            <HeaderCell>Ampel</HeaderCell>
                            <HeaderCell>Umsatz/Std<br/><span className="text-orange-400 font-bold normal-case mt-1 font-mono-numbers">Soll {calculations.sollumsatzPStd.toFixed(0)}</span></HeaderCell>
                            <HeaderCell>Plan<br/>brutto</HeaderCell>
                            <HeaderCell>+/-<br/>Netto</HeaderCell>
                            <HeaderCell></HeaderCell>
                            <HeaderCell className="border-r-0">Fest</HeaderCell>
                        </tr>
                    </thead>
                    <tbody className="text-base text-slate-200">
                        {data.map((monat: ULMonatsDaten, index: number) => {
                            const details = monatsDetails[index];
                            return (
                                <tr key={monat.monat} className={`hover:bg-pe-hover dark:hover:bg-[#111] border-b border-pe-border-strong transition-colors ${monat.festmachen ? 'bg-pe-sunken' : 'bg-transparent'}`}>
                                    <DataCell className={`sticky left-0 z-20 text-pe-muted dark:text-slate-400 font-bold text-[10px] uppercase tracking-widest border-r-2 border-pe-border dark:border-[#222] ${monat.festmachen ? 'bg-pe-sunken' : 'bg-pe-tile-header'}`}><div className="px-2 text-left">{monat.monat}</div></DataCell>
                                    <DataCell data-tooltip-id="app-tooltip" data-tooltip-content="Eingabe: Erwirtschafteter Bedienumsatz des Unternehmers in diesem Monat."><EditableField className="h-8 text-sm p-1 w-24 bg-pe-input border-stone-300 dark:border-white/10 text-stone-900 dark:text-white font-mono-numbers justify-center" variant="revenue" type="currency" value={getSafeVal(monat, 'bedienumsatzBrutto', 'bedienUmsatz')} onChange={(v) => handleUpdate(index, 'bedienumsatzBrutto', v as number)} currency={currencyCode} readOnly={isHalbjahrInputLocked} /></DataCell>
                                    <DataCell data-tooltip-id="app-tooltip" data-tooltip-content="Eingabe: Erwirtschafteter Verkaufsumsatz des Unternehmers in diesem Monat."><EditableField className="h-8 text-sm p-1 w-24 bg-pe-input border-stone-300 dark:border-white/10 text-stone-900 dark:text-white font-mono-numbers justify-center" variant="revenue" type="currency" value={getSafeVal(monat, 'vkUmsatzBrutto', 'vkUmsatz')} onChange={(v) => handleUpdate(index, 'vkUmsatzBrutto', v as number)} currency={currencyCode} readOnly={isHalbjahrInputLocked} /></DataCell>
                                    <DataCell data-tooltip-id="app-tooltip" data-tooltip-content={vkFaktorTooltip}>
                                        <div className={`w-full h-full flex items-center justify-center font-bold font-mono-numbers ${getVkFaktorColor(details.vkFaktor * 100)}`}>
                                            {showFormulas ? "Formel" : new Intl.NumberFormat('de-DE', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(details.vkFaktor || 0)}
                                        </div>
                                    </DataCell>
                                    <DataCell data-tooltip-id="app-tooltip" data-tooltip-content="Eingabe: Anzahl der genommenen Urlaubstage des Unternehmers in diesem Monat."><EditableField className="h-8 text-sm p-1 w-24 bg-pe-input border-stone-300 dark:border-white/10 text-stone-900 dark:text-white font-mono-numbers justify-center" variant="days" type="number" suffix="Tage" value={monat.urlaubsTage} onChange={(v) => handleUpdate(index, 'urlaubsTage', v as number)} currency={currencyCode} readOnly={isHalbjahrInputLocked} /></DataCell>
                                    <DataCell data-tooltip-id="app-tooltip" data-tooltip-content="Eingabe: Anzahl der Krankheitstage des Unternehmers in diesem Monat."><EditableField className="h-8 text-sm p-1 w-24 bg-pe-input border-stone-300 dark:border-white/10 text-stone-900 dark:text-white font-mono-numbers justify-center" variant="days" type="number" suffix="Tage" value={monat.krankheitsTage} onChange={(v) => handleUpdate(index, 'krankheitsTage', v as number)} currency={currencyCode} readOnly={isHalbjahrInputLocked} /></DataCell>
                                    <DataCell data-tooltip-id="app-tooltip" data-tooltip-content="Errechnet: Summe aus Bedienung und Verkauf."><FormulaDisplay show={showFormulas} value={details.gesamtumsatzBrutto} formula="Summe" currencyCode={currencyCode} /></DataCell>
                                    <DataCell>{!details.isZeileLeer && (<UmsatzIndicatorWithText umsatz={details.gesamtumsatzBrutto} soll={details.activePlanumsatz} kostendeckung={details.activeKostendeckung} />)}</DataCell>
                                    <DataCell data-tooltip-id="app-tooltip" data-tooltip-content="Errechnet: Gesamtumsatz geteilt durch die produktiven Monatsstunden des Unternehmers."><FormulaDisplay show={showFormulas} value={details.umsatzPStd} formula="Std-U" decimalPlaces={0} textColor="text-orange-400" currencyCode={currencyCode} /></DataCell>
                                    <DataCell data-tooltip-id="app-tooltip" data-tooltip-content="Vorgegebener Planumsatz für diesen Monat."><EditableField className="h-8 text-sm p-1 w-24 bg-transparent border-none text-pe-muted dark:text-slate-400 font-mono-numbers justify-center pointer-events-none" variant="revenue" type="currency" value={details.activePlanumsatz} readOnly onChange={() => {}} currency={currencyCode} /></DataCell>
                                    <DataCell data-tooltip-id="app-tooltip" data-tooltip-content="Differenz zwischen dem erwirtschafteten Netto und dem Zielumsatz (Planumsatz)."><FormulaDisplay show={showFormulas} value={details.deltaNetto} formula="Diff" highlightNegative currencyCode={currencyCode} /></DataCell>
                                    <DataCell></DataCell>
                                    <DataCell className="border-r-0 text-center" data-tooltip-id="app-tooltip" data-tooltip-content="Schließt den Monat ab und friert die Basis-Kennzahlen für diesen Monat unwiderruflich ein."><input type="checkbox" checked={monat.festmachen} onClick={(e) => handleFestmachenChange(e as React.MouseEvent<HTMLInputElement>, index, monat.festmachen)} readOnly disabled={isHalbjahrInputLocked} className="form-checkbox h-5 w-5 cursor-pointer bg-pe-input dark:bg-black border-pe-border-strong dark:border-[#444] rounded text-emerald-500 focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed" /></DataCell>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                
                <div style={{ width: `${TOTAL_WIDTH}px`, minWidth: `${TOTAL_WIDTH}px` }} className="text-base font-bold font-sans bg-transparent">
                    <div className="flex bg-pe-sunken font-bold border-t-2 border-pe-border-strong dark:border-[#444] h-20 items-stretch">
                         <div style={{ width: `${COLS.monat}px` }} className="p-1 flex items-center border-r-2 border-pe-border-strong sticky left-0 z-20 bg-pe-sunken dark:bg-black text-pe-muted dark:text-slate-400 text-[10px] uppercase tracking-widest pl-2">Gesamt</div>
                         <FooterCell width={COLS.bedien} bg="bg-transparent"><div data-tooltip-id="app-tooltip" data-tooltip-content="Summe aller Bedienumsätze im Halbjahr." className="w-full h-full flex items-center"><FormulaDisplay show={showFormulas} value={summary.bedienumsatzBrutto} formula="Summe" currencyCode={currencyCode} textColor="text-pe-text" /></div></FooterCell>
                         <FooterCell width={COLS.vk} bg="bg-transparent"><div data-tooltip-id="app-tooltip" data-tooltip-content="Summe aller Verkaufsumsätze im Halbjahr." className="w-full h-full flex items-center"><FormulaDisplay show={showFormulas} value={summary.vkUmsatzBrutto} formula="Summe" currencyCode={currencyCode} textColor="text-pe-text" /></div></FooterCell>
                         <FooterCell width={COLS.vkFaktor} />
                         <FooterCell width={COLS.urlaub} bg="bg-transparent"><div data-tooltip-id="app-tooltip" data-tooltip-content="Gesamte Urlaubstage im Halbjahr." className="w-full h-full flex items-center"><FormulaDisplay show={showFormulas} value={summary.urlaubsTage} type="number" decimalPlaces={0} formula="Summe" suffix="Tage" textColor="text-blue-500/80"/></div></FooterCell>
                         <FooterCell width={COLS.krank} bg="bg-transparent"><div data-tooltip-id="app-tooltip" data-tooltip-content="Gesamte Krankheitstage im Halbjahr." className="w-full h-full flex items-center"><FormulaDisplay show={showFormulas} value={summary.krankheitsTage} type="number" decimalPlaces={0} formula="Summe" suffix="Tage" textColor="text-red-500/80"/></div></FooterCell>
                         <FooterCell width={COLS.gesamt} bg="bg-transparent"><div data-tooltip-id="app-tooltip" data-tooltip-content="Summe aller Gesamtumsätze im Halbjahr." className="w-full h-full flex items-center"><FormulaDisplay show={showFormulas} value={summary.gesamtumsatzBrutto} formula="Summe" currencyCode={currencyCode} textColor="text-pe-text" /></div></FooterCell>
                         
                         <div style={{ width: `${COLS.ampel}px` }} className="relative flex items-center justify-center border-r border-pe-border-strong overflow-visible z-10" data-tooltip-id="app-tooltip" data-tooltip-content="Ø Umsatzauslastung-Matrix">
                                <div className="flex flex-col items-center justify-center relative z-20 pointer-events-none w-full">
                                    <div className="text-[9px] text-stone-500 dark:text-slate-500 font-bold mb-1 uppercase tracking-widest">Ø Umsatzauslastung</div>
                                    <div className={`w-28 h-10 flex items-center justify-center border-2 rounded-lg bg-pe-tile-header dark:bg-black text-xl font-black font-mono-numbers ${auslastungBorder} ${auslastungColor}`}>
                                        {showFormulas ? "(Ist/Soll)*Faktor" : new Intl.NumberFormat('de-DE', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 }).format((umsatzAuslastung <= 5 ? umsatzAuslastung : umsatzAuslastung/100))}
                                    </div>
                                </div>
                         </div>

                         <FooterCell width={COLS.umsatzStd} bg="bg-transparent">
                             {(summary.gesamtumsatzBrutto || 0) > 0 && (
                                 <div className="bg-pe-tile-header dark:bg-black p-2 rounded-lg border border-pe-border-strong shadow-inner flex flex-col justify-center text-[9px] text-left leading-tight w-[130px] pointer-events-none">
                                     <div className="text-stone-500 dark:text-slate-500 uppercase font-bold mb-1.5 border-b border-pe-border dark:border-[#222] pb-1 tracking-widest">Ziel: 70-75%</div>
                                     <div className="text-purple-400 font-bold mb-0.5">≥ 85% = Exzellent</div>
                                     <div className="text-emerald-400 font-bold mb-0.5">≥ 75% = Sehr Gut</div>
                                     <div className="text-lime-400 font-bold mb-0.5">≥ 70% = Gut</div>
                                     <div className="text-amber-400 font-bold mb-0.5">≥ 60% = Akzep.</div>
                                     <div className="text-orange-400 font-bold mb-0.5">≥ 50% = Kritisch</div>
                                     <div className="text-red-400 font-bold">&lt; 50% = Defizitär</div>
                                 </div>
                             )}
                         </FooterCell>

                         <FooterCell width={COLS.plan} />
                         <FooterCell width={COLS.netto}><div data-tooltip-id="app-tooltip" data-tooltip-content="Summe der Abweichungen vom Planumsatz (Netto)." className="w-full h-full flex items-center"><FormulaDisplay show={showFormulas} value={summary.deltaNetto} formula="Summe" highlightNegative currencyCode={currencyCode} /></div></FooterCell>
                         <FooterCell width={COLS.gap}></FooterCell>
                         <FooterCell width={COLS.fest} className="border-r-0"></FooterCell>
                    </div>

                    <div className="flex bg-pe-card dark:bg-[#111] font-bold border-t border-pe-border-strong h-12 items-center">
                         <div style={{ width: `${COLS.monat}px` }} className="p-1 flex items-center border-r-2 border-pe-border dark:border-[#222] sticky left-0 z-20 bg-pe-tile-header text-phantom-gold text-[10px] uppercase tracking-widest pl-2">Schnitt</div>
                         <FooterCell width={COLS.bedien} bg="bg-transparent"><div data-tooltip-id="app-tooltip" data-tooltip-content="Durchschnittlicher Bedienumsatz." className="w-full h-full flex items-center"><FormulaDisplay show={showFormulas} value={average.bedienumsatzBrutto} formula="Ø" currencyCode={currencyCode} textColor="text-slate-300"/></div></FooterCell>
                         <FooterCell width={COLS.vk} bg="bg-transparent"><div data-tooltip-id="app-tooltip" data-tooltip-content="Durchschnittlicher Verkaufsumsatz." className="w-full h-full flex items-center"><FormulaDisplay show={showFormulas} value={average.vkUmsatzBrutto} formula="Ø" currencyCode={currencyCode} textColor="text-slate-300"/></div></FooterCell>
                         <FooterCell width={COLS.vkFaktor} bg="bg-transparent"><div data-tooltip-id="app-tooltip" data-tooltip-content={vkFaktorTooltip} className="w-full h-full flex items-center"><FormulaDisplay show={showFormulas} value={average.vkFaktor} formula="Ø" type="percentage" decimalPlaces={1} textColor={getVkFaktorColor(average.vkFaktor * 100)} currencyCode={currencyCode} /></div></FooterCell>
                         <FooterCell width={COLS.urlaub}><div className="w-full text-center text-stone-500 dark:text-slate-500">-</div></FooterCell>
                         <FooterCell width={COLS.krank}><div className="w-full text-center text-stone-500 dark:text-slate-500">-</div></FooterCell>
                         <FooterCell width={COLS.gesamt} bg="bg-transparent"><div data-tooltip-id="app-tooltip" data-tooltip-content="Durchschnittlicher Gesamtumsatz." className="w-full h-full flex items-center"><FormulaDisplay show={showFormulas} value={average.gesamtumsatzBrutto} formula="Ø" currencyCode={currencyCode} textColor="text-pe-text"/></div></FooterCell>
                         
                         <FooterCell width={COLS.ampel}><UmsatzIndicatorWithText umsatz={average.gesamtumsatzBrutto} soll={average.activePlanumsatz} kostendeckung={average.activeKostendeckung} /></FooterCell>
                         
                         <FooterCell width={COLS.umsatzStd} bg="bg-transparent"><div data-tooltip-id="app-tooltip" data-tooltip-content="Durchschnittlicher Umsatz pro Stunde." className="w-full h-full flex items-center"><FormulaDisplay show={showFormulas} value={average.umsatzPStd} formula="Ø" decimalPlaces={0} currencyCode={currencyCode} textColor="text-orange-400"/></div></FooterCell>
                         
                         <FooterCell width={COLS.plan}><div data-tooltip-id="app-tooltip" data-tooltip-content="Durchschnittlicher Planumsatz." className="w-full h-full flex items-center justify-end pr-1"><span className="text-pe-muted dark:text-slate-400 font-bold font-mono-numbers">{new Intl.NumberFormat('de-DE').format(average.activePlanumsatz)} {currencyCode}</span></div></FooterCell>
                         
                         <FooterCell width={COLS.netto}><div data-tooltip-id="app-tooltip" data-tooltip-content="Durchschnittliche Abweichung (Netto)." className="w-full h-full flex items-center"><FormulaDisplay show={showFormulas} value={average.deltaNetto} formula="Ø" highlightNegative currencyCode={currencyCode} textColor="text-slate-300"/></div></FooterCell>
                         <FooterCell width={COLS.gap}></FooterCell>
                         <FooterCell width={COLS.fest} className="border-r-0"></FooterCell>
                    </div>

                    <div className="flex bg-pe-sunken font-bold border-t border-pe-border-strong h-12 items-center">
                         <div style={{ width: `${COLS.monat}px` }} className="p-1 flex items-center border-r-2 border-pe-border dark:border-[#222] sticky left-0 z-20 bg-pe-tile-header text-stone-500 dark:text-slate-500 uppercase tracking-widest text-[9px] pl-2">Arbeitsleistung Ø</div>
                         <div style={{ width: `${COLS.bedien + COLS.vk}px` }} className="p-1 flex items-center justify-center border-r border-pe-border dark:border-[#222]" data-tooltip-id="app-tooltip" data-tooltip-content="Die reale durchschnittliche Arbeitsleistung pro Monat.">
                             <FormulaDisplay 
                                show={showFormulas} 
                                formula="Ø Bedien + Ø VK" 
                                value={(average.bedienumsatzBrutto || 0) + (average.vkUmsatzBrutto || 0)} 
                                type="currency" 
                                textColor="text-pe-muted dark:text-slate-400" 
                                currencyCode={currencyCode} 
                             />
                         </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

interface UnternehmerleistungSeiteProps {
    navigateTo: (pageKey: string) => void;
}

const UnternehmerleistungSeite: React.FC<UnternehmerleistungSeiteProps> = ({ navigateTo }) => {
    const { clientData, setClientData, settings, showFormulas, updateULMonat, isHJ2Freigegeben, isHJ1GesperrtGlobal } = useAppStore(useShallow(state => ({ 
        clientData: state.clientData, 
        setClientData: state.setClientData,
        settings: state.settings, 
        showFormulas: state.showFormulas, 
        updateULMonat: state.updateULMonat, 
        isHJ2Freigegeben: state.clientData.isHJ2Freigegeben,
        isHJ1GesperrtGlobal: state.clientData.isHJ1GesperrtGlobal,
    })));
    const hj1ClientLocked = isHj1ClientLocked({ isHJ1GesperrtGlobal, isHJ2Freigegeben });
    const hj1LockedForUi = hj1ClientLocked;
    const frozenUnternehmerHJ1 = (clientData as any).frozenUnternehmerStammdatenHJ1;
    
    // Globale Daten
    const calculationData = useCalculationsSelector();
    const globalMwst =
        calculationData?.mwstSatz !== undefined && calculationData?.mwstSatz !== null
            ? Number(calculationData.mwstSatz)
            : clientData.isVatExempt
                ? 0
                : (settings.country?.MwSt_Satz ?? 19.0);
    const AUSLASTUNGS_FAKTOR = (settings.global.Auslastungsfaktor || 75) / 100; 
    const CURRENCY_CODE = settings.country?.currency || 'EUR';
    const firstEmployeeId = useMemo(() => {
        const id = clientData.personalData?.[0]?.id;
        return id ? String(id) : null;
    }, [clientData.personalData]);

    // 💉 CHIRURGIE: Multi-Founder Weiche
    const pgData = clientData.personengesellschaft || { aktiv: false, partner: [] };
    const isMultiFounder = pgData.aktiv;
    const [activeTabId, setActiveTabId] = useState<string | null>(null);

    // 💉 CHIRURGIE: Solo-Weiche für Halbjahr 2
    const isSolo = clientData.isSoloBusiness === true || !clientData.personalData || clientData.personalData.length === 0;
    const showHJ2 = isHJ2Freigegeben || isSolo;

    useEffect(() => {
        if (isMultiFounder && !activeTabId && pgData.partner.length > 0) {
            setActiveTabId(pgData.partner[0].id);
        }
    }, [isMultiFounder, pgData.partner, activeTabId]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const activePartner = isMultiFounder ? pgData.partner.find((p: any) => p.id === activeTabId) : null;

    // 💉 CHIRURGIE: Lokale Aktualisierungs-Funktion für das Tab-System
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const localUpdateULMonat = (halbjahrKey: 'ulHalbjahr1' | 'ulHalbjahr2', index: number, field: string, value: any) => {
        if (halbjahrKey === 'ulHalbjahr1' && hj1LockedForUi) {
            return;
        }

        if (isMultiFounder && activePartner) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setClientData((prev: any) => {
                const pg = prev.personengesellschaft;
                if (!pg) return prev;
                return {
                    ...prev,
                    personengesellschaft: {
                        ...pg,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        partner: pg.partner.map((p: any) => {
                            if (p.id !== activePartner.id) return p;
                            const currentHj = p[halbjahrKey] || (halbjahrKey === 'ulHalbjahr1' ? getEmptyHJ1() : getEmptyHJ2());
                            const newHj = [...currentHj];
                            newHj[index] = { ...newHj[index], [field]: value };
                            return { ...p, [halbjahrKey]: newHj };
                        })
                    }
                };
            });
        } else {
            updateULMonat(halbjahrKey, index, field, value);
        }
    };

    // Daten-Sourcing abhängig vom aktiven Modus
    const activeUL1 = (isMultiFounder && activePartner) ? (activePartner.ulHalbjahr1 || getEmptyHJ1()) : clientData.ulHalbjahr1;
    const activeUL2 = (isMultiFounder && activePartner) ? (activePartner.ulHalbjahr2 || getEmptyHJ2()) : clientData.ulHalbjahr2;

    // Live-Kennzahlen (immer aktuell — Basis für HJ2 und für HJ1 solange noch nicht eingefroren)
    let liveMetrics = {
        prodUnternehmerlohn: calculationData.prodUnternehmerlohn || 0,
        planumsatzBrutto: calculationData.erwarteterBruttoUmsatz || 0,
        kostendeckungsumsatz: (calculationData.prodUnternehmerlohn || 0) * (clientData.lohnfaktorData?.kostendeckungsfaktor1 || 2.8),
        wochenstunden: clientData.unternehmerData?.prodWochenstunden || 0,
        arbeitstageWoche: clientData.unternehmerData?.prodWochentage || 0,
        monatsstunden: (clientData.unternehmerData?.prodWochenstunden || 0) * (calculationData.wochensatz || 4.33),
        stdLohn: calculationData.stundenlohnChef || 0,
        sollumsatzPStd: calculationData.bruttoumsatzProStunde || 0,
        sollumsatzPStd75: (calculationData.bruttoumsatzProStunde || 0) / 0.75,
        sollumsatzProTag: (calculationData.bruttoumsatzProStunde || 0) * ((clientData.unternehmerData?.prodWochenstunden || 0) / (clientData.unternehmerData?.prodWochentage || 1)),
        arbeitszeitTag: (clientData.unternehmerData?.prodWochenstunden || 0) / (clientData.unternehmerData?.prodWochentage || 1),
    };

    if (isMultiFounder && activePartner) {
        const entnahme = activePartner.entnahme || 0;
        const sozial = activePartner.sozialkosten || 0;
        const prodLohn = entnahme + sozial; 

        const wochenstunden = activePartner.prodWochenstunden || 0;
        const arbeitstageWoche = activePartner.prodWochentage || 1; 
        const monatsstunden = wochenstunden * (calculationData.wochensatz || 4.33);
        const stdLohn = monatsstunden > 0 ? prodLohn / monatsstunden : 0;
        
        const avgUmsatz = activePartner.durchschnittsumsatz || 0;
        let expectedBrutto = avgUmsatz;
        if (expectedBrutto === 0) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const activeMonths = (activePartner.monatsUmsaetze || []).filter((m: number) => m > 0);
            expectedBrutto = activeMonths.length > 0 ? activeMonths.reduce((a: number,b: number)=>a+b,0)/activeMonths.length : 0;
        }

        const sollumsatzPStd = monatsstunden > 0 ? expectedBrutto / monatsstunden : 0;
        const arbeitszeitTag = wochenstunden / arbeitstageWoche;

        liveMetrics = {
            prodUnternehmerlohn: prodLohn,
            planumsatzBrutto: expectedBrutto,
            kostendeckungsumsatz: prodLohn * (clientData.lohnfaktorData?.kostendeckungsfaktor1 || 2.8),
            wochenstunden,
            arbeitstageWoche,
            monatsstunden,
            stdLohn,
            sollumsatzPStd,
            sollumsatzPStd75: sollumsatzPStd / 0.75,
            sollumsatzProTag: sollumsatzPStd * arbeitszeitTag,
            arbeitszeitTag,
        };
    }

    // HJ1: nach Freischaltung fest eingefrorene Stammdaten — sonst live
    let metricsHJ1 = liveMetrics;
    if (hj1LockedForUi && frozenUnternehmerHJ1) {
        if (isMultiFounder && activePartner?.id && frozenUnternehmerHJ1.partners?.[activePartner.id]) {
            const fp = frozenUnternehmerHJ1.partners[activePartner.id];
            metricsHJ1 = {
                prodUnternehmerlohn: fp.prodUnternehmerlohn,
                planumsatzBrutto: fp.planumsatzBrutto,
                kostendeckungsumsatz: fp.kostendeckungsumsatz,
                wochenstunden: fp.prodWochenstunden,
                arbeitstageWoche: fp.prodWochentage,
                monatsstunden: fp.monatsstunden,
                stdLohn: fp.stdLohn,
                sollumsatzPStd: fp.sollumsatzPStd,
                sollumsatzPStd75: fp.sollumsatzPStd75,
                sollumsatzProTag: fp.sollumsatzProTag,
                arbeitszeitTag: fp.arbeitszeitTag,
            };
        } else if (!isMultiFounder || !activePartner) {
            metricsHJ1 = {
                prodUnternehmerlohn: frozenUnternehmerHJ1.prodUnternehmerlohn,
                planumsatzBrutto: frozenUnternehmerHJ1.planumsatzBrutto,
                kostendeckungsumsatz: frozenUnternehmerHJ1.kostendeckungsumsatz,
                wochenstunden: frozenUnternehmerHJ1.prodWochenstunden,
                arbeitstageWoche: frozenUnternehmerHJ1.prodWochentage,
                monatsstunden: frozenUnternehmerHJ1.monatsstunden,
                stdLohn: frozenUnternehmerHJ1.stdLohn,
                sollumsatzPStd: frozenUnternehmerHJ1.sollumsatzPStd,
                sollumsatzPStd75: frozenUnternehmerHJ1.sollumsatzPStd75,
                sollumsatzProTag: frozenUnternehmerHJ1.sollumsatzProTag,
                arbeitszeitTag: frozenUnternehmerHJ1.arbeitszeitTag,
            };
        }
    }

    // HJ2 läuft immer auf aktuellen (live) Stammdaten
    const metricsHJ2 = liveMetrics;

    const nameLive = (isMultiFounder && activePartner)
        ? (activePartner.name || 'Partner')
        : (clientData.unternehmername || 'Kein Name');

    const nameHJ1 = (isMultiFounder && activePartner)
        ? ((hj1LockedForUi && frozenUnternehmerHJ1?.partners?.[activePartner.id]?.name) || nameLive)
        : ((hj1LockedForUi && frozenUnternehmerHJ1?.name) || nameLive);

    const [passwordPrompt, setPasswordPrompt] = useState({ isOpen: false, onSuccess: null as (() => void) | null });
    const [festSessionUnlocked, setFestSessionUnlocked] = useState(false);
    const [isHistorieOpen, setIsHistorieOpen] = useState(false);
    const [isHistorieHJ2Open, setIsHistorieHJ2Open] = useState(false);

    const hasFestmachenMonateHJ1 = useMemo(
        () => (activeUL1 || []).some((m: ULMonatsDaten) => m?.festmachen),
        [activeUL1]
    );

    const hasFestmachenMonateHJ2 = useMemo(
        () => (activeUL2 || []).some((m: ULMonatsDaten) => m?.festmachen),
        [activeUL2]
    );

    const hj1HistorieEntries = useMemo(
        () => mapUnternehmerHistorieEntries(activeUL1),
        [activeUL1]
    );

    const hj2HistorieEntries = useMemo(
        () => mapUnternehmerHistorieEntries(activeUL2),
        [activeUL2]
    );

    const handleClosePasswordPrompt = () => { setPasswordPrompt({ isOpen: false, onSuccess: null }); };
    
    const handlePasswordSubmit = (password: string) => {
        if (password === '90') {
            setFestSessionUnlocked(true);
            if (passwordPrompt.onSuccess) passwordPrompt.onSuccess();
        } else {
            alert("Falsches Passwort.");
        }
        setPasswordPrompt({ isOpen: false, onSuccess: null });
    };

    const applyFestmachenToggle = (halbjahrKey: 'ulHalbjahr1' | 'ulHalbjahr2', index: number, currentState: boolean) => {
        const targetState = !currentState;
        const snapMetrics = halbjahrKey === 'ulHalbjahr1' ? metricsHJ1 : metricsHJ2;

        if (targetState) {
            const snap = {
                isLocked: true,
                lockedAt: new Date().toISOString(),
                lockedBy: 'Admin',
                metrics: {
                    monatsstunden: snapMetrics.monatsstunden,
                    planumsatz: snapMetrics.planumsatzBrutto,
                    mwst: globalMwst,
                    kostendeckung: snapMetrics.kostendeckungsumsatz,
                    arbeitstage: snapMetrics.arbeitstageWoche,
                    wochenstunden: snapMetrics.wochenstunden,
                    stdLohn: snapMetrics.stdLohn,
                    sollumsatzPStd: snapMetrics.sollumsatzPStd,
                }
            };
            localUpdateULMonat(halbjahrKey, index, 'lockedSnapshot', snap);
        } else {
            localUpdateULMonat(halbjahrKey, index, 'lockedSnapshot', undefined);
        }

        localUpdateULMonat(halbjahrKey, index, 'festmachen', targetState);
    };

    const requestPasswordAndToggle = (halbjahrKey: 'ulHalbjahr1' | 'ulHalbjahr2', index: number, currentState: boolean) => {
        // HJ1 hart gesperrt (auch Gott-Modus) — nur Unternehmer-Seite, kein MA-Bulk
        if (halbjahrKey === 'ulHalbjahr1' && hj1LockedForUi) {
            return;
        }
        if (festSessionUnlocked) {
            applyFestmachenToggle(halbjahrKey, index, currentState);
            return;
        }
        setPasswordPrompt({
            isOpen: true,
            onSuccess: () => applyFestmachenToggle(halbjahrKey, index, currentState),
        });
    };

    const handleNavigateToMitarbeiter1 = () => { 
        if (firstEmployeeId) { 
            navigateTo(`leistung-${firstEmployeeId}`); 
        }
    };

    return (
        <div id="main-scroll-container" className="relative w-full min-h-[calc(100vh-160px)] h-auto overflow-visible bg-deep-space selection:bg-phantom-gold selection:text-black pb-28">
            <DashboardStyles />
            {/* Breite = Inhalt + moderater rechter Scroll-Puffer */}
            <div
                className="px-4 md:px-6 lg:px-8 py-4 pb-28 flex flex-col items-start font-sans text-slate-200"
                style={{ width: 'max-content', minWidth: '2300px', paddingRight: '9rem' }}
            >
                
                <SafePasswordPrompt 
                    isOpen={passwordPrompt.isOpen} 
                    onClose={handleClosePasswordPrompt} 
                    onConfirm={handlePasswordSubmit}
                    title={passwordPrompt.isOpen ? "Monat entsperren/festmachen" : "Passwortabfrage"}
                />
                {festSessionUnlocked && (
                    <div className="mb-3 text-[10px] font-bold uppercase tracking-widest text-emerald-400/90">
                        Festsetzen freigeschaltet — Passwort gilt bis zum Verlassen dieser Seite
                    </div>
                )}

                {/* 💉 CHIRURGIE: TABS FÜR MULTI-FOUNDER */}
                {isMultiFounder && (
                    <div className="flex flex-wrap gap-2 mb-6 w-full max-w-6xl border-b border-slate-800 pb-2">
                        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                        {pgData.partner.map((p: any) => (
                            <button 
                                key={p.id}
                                onClick={() => setActiveTabId(p.id)}
                                className={`px-6 py-3 rounded-t-lg font-bold text-sm uppercase tracking-widest transition-colors ${
                                    activeTabId === p.id 
                                        ? 'bg-pe-card dark:bg-slate-900 text-pe-accent-text border-t border-x border-orange-500/50 shadow-[0_-5px_15px_rgba(249,115,22,0.1)]' 
                                        : 'bg-pe-sunken dark:bg-black/50 text-stone-500 dark:text-slate-500 hover:bg-pe-hover dark:hover:bg-slate-800 hover:text-pe-text dark:hover:text-slate-300'
                                }`}
                            >
                                Leistung: {p.name || 'Partner'}
                            </button>
                        ))}
                    </div>
                )}
                
                <Hj1MandantLockBanner show={hj1ClientLocked} />

                <div className="flex flex-row gap-4 items-start w-full max-w-6xl mb-6">
                    <UI.Section className="max-w-6xl w-full p-0 border-none bg-transparent">
                        <UnternehmerKennzahlenSektion 
                            title={hj1LockedForUi ? 'Stammdaten 1. Halbjahr (eingefroren)' : 'Stammdaten (Unternehmer)'}
                            echterUnternehmername={nameHJ1} 
                            echterPrdUnternehmerLohn={metricsHJ1.prodUnternehmerlohn} 
                            echterPlanumsatzBrutto={metricsHJ1.planumsatzBrutto} 
                            calculations={metricsHJ1} 
                            showFormulas={showFormulas} 
                            currencyCode={CURRENCY_CODE} 
                        />
                    </UI.Section>
                    {hasFestmachenMonateHJ1 && (
                        <div className="ml-auto self-end shrink-0 flex flex-col gap-3">
                            <button
                                type="button"
                                onClick={() => setIsHistorieOpen(true)}
                                className="bg-pe-tile-header dark:bg-[#1a1a1a] hover:bg-pe-hover dark:hover:bg-[#222] text-pe-accent-text font-bold py-3 px-6 rounded-xl transition-all flex items-center justify-center gap-3 border border-pe-border-strong shadow-lg"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12 6 12 12 16 14"></polyline>
                                </svg>
                                Festsetzungs-Historie
                            </button>
                        </div>
                    )}
                </div>

                <div className="w-full mb-6">
                    <HalbjahrTabelleSektion 
                        title={`1. Halbjahr ${isMultiFounder && activePartner ? `(${activePartner.name})` : ''}`} 
                        halbjahrKey="ulHalbjahr1" 
                        data={activeUL1} 
                        localUpdateULMonat={localUpdateULMonat} 
                        showFormulas={showFormulas} 
                        calculations={metricsHJ1} 
                        onToggleFestmachen={requestPasswordAndToggle} 
                        planumsatzBrutto={metricsHJ1.planumsatzBrutto} 
                        mwstProzent={globalMwst} 
                        auslastungsFaktor={AUSLASTUNGS_FAKTOR} 
                        currencyCode={CURRENCY_CODE} 
                        kostendeckungsumsatz={metricsHJ1.kostendeckungsumsatz}
                        isHalbjahrInputLocked={hj1LockedForUi}
                    />
                </div>
                
                {showHJ2 ? (
                    <div className="w-full mb-6">
                        {hj1LockedForUi && (
                            <div className="flex flex-row gap-4 items-start w-full max-w-6xl mb-6 pt-6 border-t-4 border-pe-border-strong">
                                <UI.Section className="max-w-6xl w-full p-0 border-none bg-transparent">
                                    <UnternehmerKennzahlenSektion 
                                        title="Stammdaten 2. Halbjahr (aktuell)"
                                        echterUnternehmername={nameLive} 
                                        echterPrdUnternehmerLohn={metricsHJ2.prodUnternehmerlohn} 
                                        echterPlanumsatzBrutto={metricsHJ2.planumsatzBrutto} 
                                        calculations={metricsHJ2} 
                                        showFormulas={showFormulas} 
                                        currencyCode={CURRENCY_CODE} 
                                    />
                                </UI.Section>
                                {hasFestmachenMonateHJ2 && (
                                    <div className="ml-auto self-end shrink-0 flex flex-col gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setIsHistorieHJ2Open(true)}
                                            className="bg-pe-tile-header dark:bg-[#1a1a1a] hover:bg-pe-hover dark:hover:bg-[#222] text-pe-accent-text font-bold py-3 px-6 rounded-xl transition-all flex items-center justify-center gap-3 border border-pe-border-strong shadow-lg"
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <polyline points="12 6 12 12 16 14"></polyline>
                                            </svg>
                                            Festsetzungs-Historie (HJ2)
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                        {!hj1LockedForUi && hasFestmachenMonateHJ2 && (
                            <div className="flex justify-end mb-4">
                                <button
                                    type="button"
                                    onClick={() => setIsHistorieHJ2Open(true)}
                                    className="bg-pe-tile-header dark:bg-[#1a1a1a] hover:bg-pe-hover dark:hover:bg-[#222] text-pe-accent-text font-bold py-3 px-6 rounded-xl transition-all flex items-center justify-center gap-3 border border-pe-border-strong shadow-lg"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <polyline points="12 6 12 12 16 14"></polyline>
                                    </svg>
                                    Festsetzungs-Historie (HJ2)
                                </button>
                            </div>
                        )}
                        <HalbjahrTabelleSektion 
                            title={`2. Halbjahr ${isMultiFounder && activePartner ? `(${activePartner.name})` : ''}`}
                            halbjahrKey="ulHalbjahr2" 
                            data={activeUL2} 
                            localUpdateULMonat={localUpdateULMonat} 
                            showFormulas={showFormulas} 
                            calculations={metricsHJ2} 
                            onToggleFestmachen={requestPasswordAndToggle} 
                            planumsatzBrutto={metricsHJ2.planumsatzBrutto} 
                            mwstProzent={globalMwst} 
                            auslastungsFaktor={AUSLASTUNGS_FAKTOR} 
                            currencyCode={CURRENCY_CODE} 
                            kostendeckungsumsatz={metricsHJ2.kostendeckungsumsatz}
                        />
                    </div>
                ) : (
                    <div className="max-w-2xl text-center p-10 carbon-panel carbon-weave-bg border border-pe-border-strong mt-10">
                        <div className="carbon-content">
                            <h2 className="text-xl font-black uppercase tracking-widest text-pe-text mb-4">Das 2. Halbjahr ist noch nicht freigeschaltet.</h2>
                            <p className="text-pe-muted dark:text-slate-400 mb-8 text-sm leading-relaxed">Die Freischaltung muss zentral durch den Administrator oder den primären Mandanten auf der Seite von Mitarbeiter 1 erfolgen.</p>
                            <button onClick={handleNavigateToMitarbeiter1} disabled={!firstEmployeeId} className="metal-badge dark:hover:bg-[#111] text-phantom-gold font-bold py-4 px-8 rounded-xl transition-colors uppercase tracking-widest text-xs border border-pe-border-strong disabled:opacity-50 disabled:cursor-not-allowed">Zur zentralen Freischaltung (Mitarbeiter 1)</button>
                        </div>
                    </div>
                )}
                <div className="h-4 w-36" aria-hidden />
            </div>

            <FestsetzungsHistorieModalUnternehmer
                isOpen={isHistorieOpen}
                onClose={() => setIsHistorieOpen(false)}
                currencyCode={CURRENCY_CODE}
                unternehmerName={nameHJ1}
                entries={hj1HistorieEntries}
                halbjahrLabel="1. Halbjahr"
            />
            <FestsetzungsHistorieModalUnternehmer
                isOpen={isHistorieHJ2Open}
                onClose={() => setIsHistorieHJ2Open(false)}
                currencyCode={CURRENCY_CODE}
                unternehmerName={nameLive}
                entries={hj2HistorieEntries}
                halbjahrLabel="2. Halbjahr"
            />
        </div>
    );
};

export default UnternehmerleistungSeite;